/**
 * CharCountField.jsx — text input / textarea wrappers with a live "n / max"
 * character counter, and (for the textarea) auto-grow-to-content.
 *
 * Limit resolution:
 *  - pass `fieldKey` to look the limit up in lib/fieldLimits.js, or
 *  - pass an explicit `limit` (+ optional `hard`, default true).
 * A HARD limit sets the native `maxLength` (blocks typing past the max — used
 * for fixed-width SQL columns). A SOFT limit only colours the counter past the
 * max (used for NVARCHAR(MAX) narratives — never truncates).
 *
 * All other props (onChange, placeholder, type, id, disabled, autoFocus, style…)
 * pass straight through to the underlying element, so these are drop-in
 * replacements for a plain `.input` / `.textarea`.
 */
import React, { useLayoutEffect, useRef } from 'react';
import { fieldLimit } from '../lib/fieldLimits.js';

function resolveLimit(fieldKey, limit, hard) {
  if (limit != null) return { max: limit, hard: hard !== false };
  if (fieldKey) return fieldLimit(fieldKey);
  return null;
}

function Counter({ len, max }) {
  const over = len > max;
  const near = !over && len >= max * 0.9;
  return (
    <div className={`char-counter${over ? ' over' : near ? ' near' : ''}`} aria-live="polite">
      {len} / {max}
    </div>
  );
}

export function CharCountInput({ fieldKey, limit, hard, value = '', className = 'input', ...rest }) {
  const lim = resolveLimit(fieldKey, limit, hard);
  return (
    <div className="char-field">
      <input
        className={className}
        value={value}
        maxLength={lim && lim.hard ? lim.max : undefined}
        {...rest}
      />
      {lim && <Counter len={String(value ?? '').length} max={lim.max} />}
    </div>
  );
}

export function CharCountTextarea({ fieldKey, limit, hard, value = '', className = 'textarea', autoGrow = true, ...rest }) {
  const lim = resolveLimit(fieldKey, limit, hard);
  const ref = useRef(null);

  // Auto-grow: reset then match scrollHeight, capped so the box scrolls past
  // ~half the viewport instead of pushing the modal off-screen. `height` is set
  // imperatively (never via the style prop) so React re-renders don't clobber it.
  useLayoutEffect(() => {
    if (!autoGrow || !ref.current) return;
    const el = ref.current;
    el.style.height = 'auto';
    const cap = typeof window !== 'undefined' ? window.innerHeight * 0.5 : 600;
    el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
  }, [value, autoGrow]);

  return (
    <div className="char-field">
      <textarea
        ref={ref}
        className={className}
        value={value}
        maxLength={lim && lim.hard ? lim.max : undefined}
        {...rest}
      />
      {lim && <Counter len={String(value ?? '').length} max={lim.max} />}
    </div>
  );
}
